<template>
  <admin-layout>
    <template slot="content">
      <header-for-list-component sectionName="Requerimentos" :items="applications" :totalItems="totalItems"
        :totalPages="totalPages" :itemsPerPage="itemsPerPage" :pageNumber.sync="pageNumber"
        @loadPage="loadPage($event)" />

      <!-- Cards de status (contador + filtro rápido) -->
      <v-layout class="status-cards mb-3" wrap>
        <v-flex v-for="card in cards" :key="card.key" class="status-card-flex pa-1">
          <v-card flat class="status-card" :class="{ active: (card.key === 'all' && !filters.status) || filters.status === card.key }"
            :style="{ borderColor: card.color }" @click="selectCard(card)">
            <div class="status-amount" :style="{ color: card.color }">{{ card.amount }}</div>
            <div class="status-title">{{ card.title }}</div>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Barra de filtros -->
      <v-layout align-center wrap class="mb-3 tour-filters">
        <v-flex xs12 md3 class="pr-2 mb-2">
          <v-text-field v-model="filters.q" solo hide-details prepend-inner-icon="mdi-magnify"
            label="Buscar por nome, CPF/CNPJ ou processo" :loading="loadingSearch" @input="typing()" />
        </v-flex>
        <v-flex xs6 md2 class="pr-2 mb-2">
          <v-autocomplete v-model="filters.application_type_id" :items="typeItems" :loading="loadingTypes"
            :search-input.sync="typeSearch" item-text="text" item-value="id" solo hide-details clearable
            label="Tipo" no-data-text="Digite para buscar" @change="applyFilters()" />
        </v-flex>
        <v-flex xs6 md2 class="pr-2 mb-2">
          <v-autocomplete v-model="filters.typology_id" :items="typologyItems" :loading="loadingTypologies"
            :search-input.sync="typologySearch" item-text="text" item-value="id" solo hide-details clearable
            label="Tipologia" no-data-text="Digite para buscar" @change="applyFilters()" />
        </v-flex>
        <v-flex xs6 md2 class="pr-2 mb-2">
          <v-autocomplete v-model="filters.locked_by_id" :items="analystItems" :loading="loadingAnalysts"
            :search-input.sync="analystSearch" item-text="text" item-value="id" solo hide-details clearable
            label="Analista" no-data-text="Digite para buscar" @change="applyFilters()" />
        </v-flex>
        <v-flex xs6 md3 class="pr-2 mb-2">
          <v-select v-model="filters.payment_status" :items="paymentOptions" item-text="text" item-value="value"
            solo hide-details clearable label="Boleto" @change="applyFilters()" />
        </v-flex>
        <v-flex xs6 md2 class="pr-2 mb-2">
          <v-menu v-model="menuFrom" :close-on-content-click="false" lazy offset-y transition="scale-transition">
            <template v-slot:activator="{ on }">
              <v-text-field v-model="filters.created_from" v-on="on" solo hide-details clearable readonly
                label="Período de" prepend-inner-icon="mdi-calendar" @click:clear="applyFilters()" />
            </template>
            <v-date-picker v-model="filters.created_from" locale="pt-br"
              @input="menuFrom = false; applyFilters()" />
          </v-menu>
        </v-flex>
        <v-flex xs6 md2 class="pr-2 mb-2">
          <v-menu v-model="menuTo" :close-on-content-click="false" lazy offset-y transition="scale-transition">
            <template v-slot:activator="{ on }">
              <v-text-field v-model="filters.created_to" v-on="on" solo hide-details clearable readonly
                label="até" prepend-inner-icon="mdi-calendar" @click:clear="applyFilters()" />
            </template>
            <v-date-picker v-model="filters.created_to" locale="pt-br" :min="filters.created_from"
              @input="menuTo = false; applyFilters()" />
          </v-menu>
        </v-flex>
        <v-flex class="text-xs-right mb-2" style="white-space: nowrap;">
          <v-btn flat color="primary" @click="clearFilters()">
            <v-icon small class="mr-1">mdi-filter-remove</v-icon> Limpar
          </v-btn>
          <v-btn icon small @click="startTour" title="Mostrar tour de novidades">
            <v-icon small>mdi-help-circle-outline</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>

      <!-- Tabela -->
      <v-layout column>
        <v-data-table :headers="headers" :items="applications" :pagination.sync="pagination"
          :custom-sort="customSort" :total-items="totalItems" hide-actions class="elevation-1">
          <template slot="items" slot-scope="props">
            <td class="text-xs-center">{{ props.item.id }}</td>
            <td class="text-xs-center">{{ props.item.application_type ? props.item.application_type.acronym : '--' }}</td>
            <td>{{ props.item.person ? props.item.person.name : '--' }}</td>
            <td class="text-xs-center">{{ props.item.folder_number || '--' }}</td>
            <td class="text-xs-center">{{ props.item.person ? formatCpfCnpj(props.item.person.cpf_cnpj) : '--' }}</td>
            <td class="text-xs-center">{{ props.item.locked_by ? props.item.locked_by.name : '—' }}</td>
            <td class="text-xs-center">{{ formatDate(props.item.created_at) }}</td>
            <td class="text-xs-center">
              <span :class="['badge', paymentSlipBadge(props.item.payment_slips[0])]">
                {{ statusPaymentSlip(props.item.payment_slips[0]) }}
              </span>
            </td>
            <td class="text-xs-center">
              <span :class="['badge', props.item.status]">{{ statusTitle(props.item.status) }}</span>
            </td>
            <td class="text-xs-center" style="white-space: nowrap;">
              <v-btn icon @click="showApplication(props.item.id)" title="Visualizar"><v-icon small>mdi-eye</v-icon></v-btn>
              <v-btn icon @click="openApplicationNewTab(props.item.id)" title="Abrir em nova aba"><v-icon small>mdi-open-in-new</v-icon></v-btn>
            </td>
          </template>
        </v-data-table>

        <v-layout v-if="applications.length > 0" align-center justify-end class="mt-2">
          <span class="caption mr-2">Exibindo {{ showingStart }} - {{ showingEnd }} de {{ totalItems }}</span>
          <v-btn icon @click="loadPage(pageNumber - 1)" :disabled="pageNumber <= 1">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn icon @click="loadPage(pageNumber + 1)" :disabled="pageNumber >= totalPages">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-layout>
      </v-layout>

      <applications-list-tour ref="tour" />
    </template>
  </admin-layout>
</template>

<script>
import AdminLayout from '@/layouts/AdminLayout';
import HeaderForListComponent from '@/components/configurations/HeaderForListComponent';
import ApplicationsListTour from '@/components/configurations/application/ApplicationsListTour';
import ApplicationService from '@/services/applications/configurations/ApplicationService';
import ApplicationTypeService from '@/services/applications/configurations/ApplicationTypeService';
import TypologyService from '@/services/applications/configurations/TypologyService';
import AdminService from '@/services/applications/configurations/AdminService';
import formatCpfCnpj from '@/utils/formatCpfCnpj';

export default {
  name: 'List',
  components: {
    AdminLayout,
    HeaderForListComponent,
    ApplicationsListTour,
  },
  mounted() {
    this.loadPage(1);
    this.fetchTypes('');
    this.fetchTypologies('');
    this.fetchAnalysts('');
    this.$nextTick(() => {
      setTimeout(() => { if (this.$refs.tour) this.$refs.tour.maybeStart(); }, 800);
    });
  },
  beforeDestroy() {
    clearTimeout(this.interval);
    Object.values(this.searchTimers).forEach(clearTimeout);
  },
  data() {
    return {
      formatCpfCnpj,
      applications: [],
      totalItems: 0,
      totalPages: 0,
      itemsPerPage: 20,
      pageNumber: 1,
      loadingSearch: false,
      interval: null,
      ready: false,
      filters: {
        q: '',
        status: '',
        application_type_id: null,
        typology_id: null,
        locked_by_id: null,
        payment_status: null,
        created_from: null,
        created_to: null,
        sort: 'id',
        direction: 'desc',
      },
      pagination: { sortBy: 'id', descending: true, rowsPerPage: 20 },
      cards: [
        { key: 'all',                title: 'TODOS',              amount: 0, color: '#1976d2' },
        { key: 'waiting_to_be_sent', title: 'A ENVIAR',           amount: 0, color: '#616161' },
        { key: 'pending',            title: 'PENDENTE',           amount: 0, color: '#e53935' },
        { key: 'waiting_for_review', title: 'A ANALISAR',          amount: 0, color: '#f9a825' },
        { key: 'in_review',          title: 'EM ANÁLISE',         amount: 0, color: '#fb8c00' },
        { key: 'approved',           title: 'APROVADO',           amount: 0, color: '#43a047' },
      ],
      paymentOptions: [
        { text: 'Pago', value: 'paid' },
        { text: 'Em aberto', value: 'unpaid' },
      ],
      typeItems: [], typologyItems: [], analystItems: [],
      typeSearch: null, typologySearch: null, analystSearch: null,
      loadingTypes: false, loadingTypologies: false, loadingAnalysts: false,
      searchTimers: {},
      menuFrom: false, menuTo: false,
      headers: [
        { text: 'ID', value: 'id', sortable: true, align: 'center' },
        { text: 'Tipo', value: 'application_type', sortable: false, align: 'center' },
        { text: 'Requerente', value: 'name', sortable: false, align: 'left' },
        { text: 'Processo', value: 'folder_number', sortable: false, align: 'center' },
        { text: 'CPF - CNPJ', value: 'cpf_cnpj', sortable: false, align: 'center' },
        { text: 'Analista', value: 'locked_by', sortable: false, align: 'center' },
        { text: 'Criado em', value: 'created_at', sortable: true, align: 'center' },
        { text: 'Boleto', value: 'payment_slip', sortable: false, align: 'center' },
        { text: 'Status', value: 'status', sortable: false, align: 'center' },
        { text: 'Ações', value: 'actions', sortable: false, align: 'center', width: '110px' },
      ],
    };
  },
  computed: {
    showingStart() {
      if (!this.totalItems) return 0;
      return (this.pageNumber - 1) * this.itemsPerPage + 1;
    },
    showingEnd() {
      return Math.min(this.pageNumber * this.itemsPerPage, this.totalItems);
    },
  },
  watch: {
    'pagination.sortBy'() { this.onSortChange(); },
    'pagination.descending'() { this.onSortChange(); },
    typeSearch(val) { this.handleSearch('types', 'typeItems', 'application_type_id', val, this.fetchTypes); },
    typologySearch(val) { this.handleSearch('typologies', 'typologyItems', 'typology_id', val, this.fetchTypologies); },
    analystSearch(val) { this.handleSearch('analysts', 'analystItems', 'locked_by_id', val, this.fetchAnalysts); },
  },
  methods: {
    requestParams() {
      const f = this.filters;
      const params = { page: this.pageNumber, limit: this.itemsPerPage, sort: f.sort, direction: f.direction };
      if (f.q) params.q = f.q;
      if (f.status) params.status = f.status;
      if (f.application_type_id) params.application_type_id = f.application_type_id;
      if (f.typology_id) params.typology_id = f.typology_id;
      if (f.locked_by_id) params.locked_by_id = f.locked_by_id;
      if (f.payment_status) params.payment_status = f.payment_status;
      if (f.created_from) params.created_from = f.created_from;
      if (f.created_to) params.created_to = f.created_to;
      return params;
    },
    loadPage(page) {
      this.pageNumber = page || this.pageNumber;
      if (!this.loadingSearch) this.$store.dispatch('setLoadingDialog', true);
      ApplicationService.all(this.requestParams())
        .then(({ data }) => {
          this.applications = data.applications || [];
          this.totalItems = data.total;
          this.totalPages = data.total_pages;
          this.updateCards(data.cards);
        })
        .catch(() => {
          this.applications = [];
          this.totalItems = 0;
          this.totalPages = 0;
        })
        .finally(() => {
          this.$store.dispatch('setLoadingDialog', false);
          this.loadingSearch = false;
          this.ready = true;
        });
    },
    applyFilters() {
      this.pageNumber = 1;
      this.loadPage(1);
    },
    typing() {
      clearTimeout(this.interval);
      this.interval = setTimeout(() => {
        this.loadingSearch = true;
        this.applyFilters();
      }, 500);
    },
    onSortChange() {
      if (!this.ready) return;
      this.filters.sort = this.pagination.sortBy || 'id';
      this.filters.direction = this.pagination.descending ? 'desc' : 'asc';
      this.applyFilters();
    },
    customSort(items) {
      return items;
    },
    selectCard(card) {
      this.filters.status = card.key === 'all' ? '' : card.key;
      this.applyFilters();
    },
    updateCards(cards) {
      if (!cards) return;
      this.cards.forEach((c) => {
        c.amount = cards[c.key] != null ? cards[c.key] : 0;
      });
    },
    clearFilters() {
      this.filters.q = '';
      this.filters.status = '';
      this.filters.application_type_id = null;
      this.filters.typology_id = null;
      this.filters.locked_by_id = null;
      this.filters.payment_status = null;
      this.filters.created_from = null;
      this.filters.created_to = null;
      this.menuFrom = false;
      this.menuTo = false;
      this.applyFilters();
    },
    debouncedSearch(kind, fn) {
      clearTimeout(this.searchTimers[kind]);
      this.searchTimers[kind] = setTimeout(fn, 400);
    },
    handleSearch(kind, itemsKey, idKey, val, fetchFn) {
      if (!val) return; // ignora blur/limpeza: mantém os itens já carregados
      const selected = this[itemsKey].find((i) => i.id === this.filters[idKey]);
      if (selected && selected.text === val) return; // ignora o "eco" da seleção
      this.debouncedSearch(kind, () => fetchFn(val));
    },
    applyItems(itemsKey, idKey, items) {
      const selectedId = this.filters[idKey];
      if (selectedId && !items.some((i) => i.id === selectedId)) {
        const prev = this[itemsKey].find((i) => i.id === selectedId);
        if (prev) items = [prev, ...items]; // preserva o item selecionado na lista
      }
      this[itemsKey] = items;
    },
    normalize(data, key) {
      if (Array.isArray(data)) return data;
      if (!data) return [];
      return data[key] || data.data || [];
    },
    fetchTypes(q) {
      this.loadingTypes = true;
      ApplicationTypeService.all({ page: 1, limit: 20, q: q || '' })
        .then(({ data }) => {
          this.applyItems('typeItems', 'application_type_id', this.normalize(data, 'application_types')
            .map((t) => ({ id: t.id, text: t.acronym ? `${t.acronym} - ${t.name}` : t.name })));
        })
        .finally(() => { this.loadingTypes = false; });
    },
    fetchTypologies(q) {
      this.loadingTypologies = true;
      TypologyService.all({ page: 1, limit: 20, q: q || '' })
        .then(({ data }) => {
          this.applyItems('typologyItems', 'typology_id', this.normalize(data, 'typologies')
            .map((t) => ({ id: t.id, text: t.code ? `${t.code} - ${t.name}` : t.name })));
        })
        .finally(() => { this.loadingTypologies = false; });
    },
    fetchAnalysts(q) {
      this.loadingAnalysts = true;
      AdminService.all({ page: 1, limit: 20, q: q || '' })
        .then(({ data }) => {
          this.applyItems('analystItems', 'locked_by_id', this.normalize(data, 'admins')
            .map((a) => ({ id: a.id, text: a.name })));
        })
        .finally(() => { this.loadingAnalysts = false; });
    },
    showApplication(id) {
      this.$store.dispatch('setLoadingDialog', true);
      this.$router.push({ name: 'AdminShowApplication', params: { id } });
    },
    openApplicationNewTab(id) {
      const route = this.$router.resolve({ name: 'AdminShowApplication', params: { id } });
      window.open(route.href, '_blank');
    },
    startTour() {
      if (this.$refs.tour) this.$refs.tour.start();
    },
    formatDate(value) {
      if (!value) return '--';
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? '--' : d.toLocaleDateString('pt-BR');
    },
    statusTitle(status) {
      const titles = {
        waiting_to_be_sent: 'A ENVIAR',
        waiting_for_review: 'A ANALISAR',
        in_review: 'EM ANÁLISE',
        pending: 'PENDENTE',
        done: 'APROVADO',
        issued: 'APROVADO',
        intimated: 'INTIMAÇÃO',
        new_application: 'NOVO',
        expired: 'EXPIRADO',
      };
      return titles[status] || status;
    },
    statusPaymentSlip(paymentSlip) {
      if (!paymentSlip) return 'Boleto não disponível';
      return paymentSlip.payment_status ? 'Pago' : 'Em aberto';
    },
    paymentSlipBadge(paymentSlip) {
      if (!paymentSlip) return 'waiting_to_be_sent';
      return paymentSlip.payment_status ? 'done' : 'pending';
    },
  },
};
</script>

<style scoped lang="scss">
.status-card-flex {
  flex: 1 1 16%;
  min-width: 120px;
}
.status-card {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 8px;
  text-align: center;
  cursor: pointer;
  transition: background-color .15s ease;
  &.active {
    background-color: #e3f0fc;
    border-width: 2px;
  }
}
.status-amount {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
}
.status-title {
  font-size: 11px;
  color: #666;
}
.badge {
  color: #fff;
  font-size: 11px;
  //padding: 2px 6px;
  //border-radius: 3px;
  white-space: nowrap;
  text-wrap: nowrap;
  &.waiting_to_be_sent { background: #616161; }
  &.waiting_for_review,
  &.in_review { background: #ffc107; }
  &.pending,
  &.intimated { background: #f44336; }
  &.done,
  &.issued { background: #4caf50; }
}

::v-deep .v-datatable tbody td,
::v-deep .v-datatable thead th {
  font-size: 11px;
  padding: 0px 12px;
}
</style>
